import type { ReactNode } from "react";
import { AlertCircle } from "lucide-react";

import { PageHeader, PageHeaderSkeleton, type Crumb } from "@/components/layout";
import { ProcessingSpinner } from "@/components/processing-spinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";

const SPEC_PILOT_CRUMB: Crumb = { label: "SpecPilot", href: "/specPilot" };

/** Same header as the loaded submittal page: "← SpecPilot / {job} / Submittal". */
function SubmittalGateFrame({
  jobId,
  jobName,
  children,
}: {
  jobId: string;
  jobName: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-4">
      <PageHeader
        title="Submittal"
        trail={[
          SPEC_PILOT_CRUMB,
          { label: jobName, href: "/specPilot/$jobId", params: { jobId } },
        ]}
      />
      {children}
    </div>
  );
}

interface SubmittalStateGateProps {
  jobId: string;
  job:
    | {
        _id: string;
        name: string;
        status: "pending" | "processing" | "ready_for_review" | "completed" | "failed";
        error?: string;
      }
    | null
    | undefined;
  submittal: { hasEditedData?: boolean } | null | undefined;
  isLoadingCSV: boolean;
  generatedData: unknown;
  editedData: unknown;
  csvError: string | null;
  isHydratingEdited: boolean;
  editedPayloadError: string | null;
  activeVersion: "generated" | "edited";
  hasEditedData: boolean;
}

export function getSubmittalStateGate({
  jobId,
  job,
  submittal,
  isLoadingCSV,
  generatedData,
  editedData,
  csvError,
  isHydratingEdited,
  editedPayloadError,
  activeVersion,
  hasEditedData,
}: SubmittalStateGateProps) {
  if (job === undefined) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton withTrail />
        <Card>
          <ProcessingSpinner message="Loading submittal..." />
        </Card>
      </div>
    );
  }

  if (job === null) {
    return (
      <div className="space-y-4">
        <PageHeader title="Job Not Found" trail={[SPEC_PILOT_CRUMB]} />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Job not found</AlertTitle>
          <AlertDescription>
            The job you are looking for does not exist or has been deleted.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (job.status === "pending" || job.status === "processing") {
    return (
      <SubmittalGateFrame jobId={jobId} jobName={job.name}>
        <Card>
          <ProcessingSpinner message="Generating submittal..." />
        </Card>
      </SubmittalGateFrame>
    );
  }

  if (submittal === undefined) {
    return (
      <SubmittalGateFrame jobId={jobId} jobName={job.name}>
        <Card>
          <ProcessingSpinner message="Loading submittal..." />
        </Card>
      </SubmittalGateFrame>
    );
  }

  if (submittal === null) {
    if (job.status === "ready_for_review") {
      return (
        <SubmittalGateFrame jobId={jobId} jobName={job.name}>
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>Submittal is missing review data</AlertTitle>
            <AlertDescription>
              The job reached review, but no submittal record is available. Return to the job
              detail page and retry generation.
            </AlertDescription>
          </Alert>
        </SubmittalGateFrame>
      );
    }

    if (job.status === "completed") {
      return (
        <SubmittalGateFrame jobId={jobId} jobName={job.name}>
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>Submittal is missing generated output</AlertTitle>
            <AlertDescription>
              The job completed, but no generated submittal record is available. Return to the job
              detail page and retry generation.
            </AlertDescription>
          </Alert>
        </SubmittalGateFrame>
      );
    }

    if (job.status === "failed") {
      return (
        <SubmittalGateFrame jobId={jobId} jobName={job.name}>
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>Submittal generation failed</AlertTitle>
            <AlertDescription>
              {job.error ?? "The submittal could not be generated. Please return to the job and try again."}
            </AlertDescription>
          </Alert>
        </SubmittalGateFrame>
      );
    }

    return (
      <SubmittalGateFrame jobId={jobId} jobName={job.name}>
        <Alert>
          <AlertCircle className="size-4" />
          <AlertTitle>No submittal generated</AlertTitle>
          <AlertDescription>
            This job does not have a submittal yet. Generate one from the job detail page.
          </AlertDescription>
        </Alert>
      </SubmittalGateFrame>
    );
  }

  // Prefer keeping the loaded grid mounted. `isLoadingCSV` / hydration flags can
  // flicker when signed download URLs rotate; only block the page when we have
  // nothing to show yet. Edited-payload waits only apply on the Edited tab so a
  // Generated view with data is not unmounted by edited-URL rotation.
  const showLoading =
    (isLoadingCSV && !generatedData) ||
    (submittal && !generatedData && !csvError) ||
    (activeVersion === "edited" &&
      hasEditedData &&
      !editedData &&
      !editedPayloadError) ||
    (activeVersion === "edited" && hasEditedData && !editedData && isHydratingEdited);

  if (showLoading) {
    return (
      <SubmittalGateFrame jobId={jobId} jobName={job.name}>
        <Card>
          <ProcessingSpinner message="Loading submittal data..." />
        </Card>
      </SubmittalGateFrame>
    );
  }

  if (csvError) {
    return (
      <SubmittalGateFrame jobId={jobId} jobName={job.name}>
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Failed to load submittal</AlertTitle>
          <AlertDescription>{csvError}</AlertDescription>
        </Alert>
      </SubmittalGateFrame>
    );
  }

  return null;
}
